import * as React from "react";
import {Slot} from "@radix-ui/react-slot";
import {cva, type VariantProps} from "class-variance-authority";
import {cn} from "../lib/utils";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-800 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    "h-12 md:h-16 rounded-xl md:rounded-2xl",
    "font-display font-medium text-xl tracking-wide px-4 whitespace-nowrap"
  ),
  {
    variants: {
      variant: {
        default:
          "bg-green-800 hover:bg-green-700 active:bg-green-900 text-white",
        secondary:
          "font-semibold bg-white text-green-800 hover:bg-green-50 hover:text-green-700 active:bg-green-700 active:text-white",
        ghost:
          "border-green-900 text-green-900 bg-transparent border border-green-900 hover:bg-green-900 hover:text-white active:bg-green-700 active:text-white active:border-transparent",
        soft: "bg-green-900/10 text-green-900 hover:bg-green-900/20 active:bg-green-800 active:text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({className, variant, asChild = false, ...props}, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({variant, className}))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export {Button, buttonVariants};
